import {
  AROUND_AREA_FEATURES,
  PHYSICAL_FEATURES,
  PhysicalInfoDetails,
} from '@exam-center/exam-location-management';
import {
  type ExamRoomRequestPhysicalAttributes,
  type FeatureCheck,
  useAdminGetDownloadSignedUrlForExamRoomPhysicalInfoLazyQuery,
} from '@exam-center/graphql';
import { useEffect, useMemo, useState } from 'react';

import { useAdminExamLocationDetail } from '../../providers';

export default function AdminExamLocationRoomPhysicalInfoDetails() {
  const { examRoomInfo } = useAdminExamLocationDetail();

  const info = examRoomInfo?.examRoomPhysicalInfo?.info as
    | ExamRoomRequestPhysicalAttributes
    | null
    | undefined;

  const [getDownloadSignedUrls] = useAdminGetDownloadSignedUrlForExamRoomPhysicalInfoLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [signedUrlMap, setSignedUrlMap] = useState<Record<string, string>>({});

  const fileNames = useMemo(() => {
    const names: string[] = [];
    if (!info) return names;

    PHYSICAL_FEATURES.filter((f) => f.hasImages).forEach((f) => {
      const feature = info[f.key as keyof ExamRoomRequestPhysicalAttributes] as
        | FeatureCheck
        | null
        | undefined;
      const imgs = feature?.images;
      if (Array.isArray(imgs)) names.push(...imgs);
    });

    AROUND_AREA_FEATURES.filter((f) => f.hasImages).forEach((f) => {
      const feature = info[f.key as keyof ExamRoomRequestPhysicalAttributes] as
        | FeatureCheck
        | null
        | undefined;
      const imgs = feature?.images;
      if (Array.isArray(imgs)) names.push(...imgs);
    });

    if (Array.isArray(info.entryExitDoors?.images))
      names.push(...(info.entryExitDoors?.images as string[]));
    if (Array.isArray(info.frontOfExamRoom?.images))
      names.push(...(info.frontOfExamRoom?.images as string[]));
    if (Array.isArray(info.insideExamRoom?.images))
      names.push(...(info.insideExamRoom?.images as string[]));

    return Array.from(new Set(names)).filter(Boolean) as string[];
  }, [info]);

  useEffect(() => {
    if (!fileNames.length) return;

    (async () => {
      const result = await getDownloadSignedUrls({
        variables: {
          getDownloadSignedUrlForExamRoomPhysicalInfoInput: {
            fileNames,
            examRoomId: examRoomInfo.id,
          },
        },
        fetchPolicy: 'no-cache',
      });

      const map = (result.data?.ecAdminGetDownloadSignedUrlForExamRoomPhysicalInfo?.signedUrls ??
        {}) as Record<string, string>;
      setSignedUrlMap(map);
    })();
  }, [fileNames, getDownloadSignedUrls, examRoomInfo.id]);

  useEffect(() => {
    const frameContent = document.getElementById('frame-content');
    if (frameContent) {
      frameContent.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  const resolvedInfo = useMemo(() => {
    if (!info) return info;
    const resolve = (names?: string[] | null) =>
      (names ?? []).map((n) => (n ? signedUrlMap[n] : '')).filter((u): u is string => Boolean(u));

    return {
      ...info,
      securityCameras: info.securityCameras
        ? { ...info.securityCameras, images: resolve(info.securityCameras.images) }
        : null,
      registrationArea: info.registrationArea
        ? { ...info.registrationArea, images: resolve(info.registrationArea.images) }
        : null,
      entryExitDoors: info.entryExitDoors
        ? { ...info.entryExitDoors, images: resolve(info.entryExitDoors.images) }
        : null,
      frontOfExamRoom: info.frontOfExamRoom
        ? { ...info.frontOfExamRoom, images: resolve(info.frontOfExamRoom.images) }
        : null,
      insideExamRoom: info.insideExamRoom
        ? { ...info.insideExamRoom, images: resolve(info.insideExamRoom.images) }
        : null,
    } as ExamRoomRequestPhysicalAttributes;
  }, [info, signedUrlMap]);

  return <PhysicalInfoDetails resolvedInfo={resolvedInfo} />;
}
